/**
 * Minimal Open Location Code (Plus Code) decoder — spec: github.com/google/open-location-code
 * Supports full codes ("7PH7XQ7R+GQ") and short codes ("XQ7R+GQ") recovered near a reference point.
 */
const ALPHABET = '23456789CFGHJMPQRVWX';
const SEP = '+';
const SEP_POS = 8;
const PAD = '0';
const PAIR_RES = [20, 1, 0.05, 0.0025, 0.000125];
const GRID_ROWS = 5, GRID_COLS = 4;

const RE = /^[23456789CFGHJMPQRVWX0]{2,8}\+[23456789CFGHJMPQRVWX]{0,7}$/i;

export function isValid(code) {
  if (!code || !RE.test(code)) return false;
  const c = code.toUpperCase();
  const sep = c.indexOf(SEP);
  if (sep > SEP_POS || sep % 2 === 1) return false;
  if (c.includes(PAD)) {
    if (sep < SEP_POS) return false;
    const m = c.match(/0+/g);
    if (m.length > 1 || m[0].length % 2 === 1) return false;
    if (!c.endsWith(SEP)) return false;
  }
  return c.length - sep - 1 !== 1;
}

export const isFull = (code) => isValid(code) && code.indexOf(SEP) === SEP_POS;
export const isShort = (code) => isValid(code) && code.indexOf(SEP) >= 0 && code.indexOf(SEP) < SEP_POS;

export function decode(code) {
  if (!isFull(code)) throw new Error('Mã Plus Code không hợp lệ');
  const c = code.toUpperCase().replace(SEP, '').replace(/0+$/, '');
  let lat = -90, lng = -180, latRes = 0, lngRes = 0;
  const pairs = Math.min(c.length, 10);
  for (let i = 0; i < pairs; i += 2) {
    const r = PAIR_RES[i / 2];
    lat += ALPHABET.indexOf(c[i]) * r;
    lng += ALPHABET.indexOf(c[i + 1]) * r;
    latRes = lngRes = r;
  }
  for (let i = 10; i < c.length; i++) {
    latRes /= GRID_ROWS; lngRes /= GRID_COLS;
    const d = ALPHABET.indexOf(c[i]);
    lat += Math.floor(d / GRID_COLS) * latRes;
    lng += (d % GRID_COLS) * lngRes;
  }
  return { lat: lat + latRes / 2, lng: lng + lngRes / 2, latRes, lngRes };
}

function encodePrefix(lat, lng, digits) {
  let la = Math.min(89.9999999, Math.max(-90, lat)) + 90;
  let lo = ((((lng + 180) % 360) + 360) % 360);
  let out = '';
  for (let i = 0; i < digits / 2; i++) {
    const r = PAIR_RES[i];
    const dl = Math.floor(la / r), dg = Math.floor(lo / r);
    la -= dl * r; lo -= dg * r;
    out += ALPHABET[dl] + ALPHABET[dg];
  }
  return out;
}

/** Recover a short code relative to a reference location (closest match). */
export function recoverNearest(shortCode, refLat, refLng) {
  const code = shortCode.toUpperCase();
  if (isFull(code)) return decode(code);
  if (!isShort(code)) throw new Error('Mã Plus Code không hợp lệ');
  const padLen = SEP_POS - code.indexOf(SEP);
  const res = Math.pow(20, 2 - padLen / 2);
  const half = res / 2;
  const prefix = encodePrefix(refLat, refLng, SEP_POS).slice(0, padLen);
  const area = decode(prefix + code);
  let { lat, lng } = area;
  if (refLat + half < lat && lat - res >= -90) lat -= res;
  else if (refLat - half > lat && lat + res <= 90) lat += res;
  if (refLng + half < lng) lng -= res;
  else if (refLng - half > lng) lng += res;
  return { lat, lng, latRes: area.latRes, lngRes: area.lngRes };
}

/** Find a plus code token inside free text. */
export function extractPlusCode(text) {
  const m = String(text).toUpperCase().match(/(?:^|[\s,])([23456789CFGHJMPQRVWX]{2,8}\+[23456789CFGHJMPQRVWX]{0,7})(?=$|[\s,])/);
  return m && isValid(m[1]) ? m[1] : null;
}
